import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Phone, Building2, Users, GitBranch, Brain, Clock, Plus, PlayCircle, CheckCircle2, AlertCircle } from 'lucide-react';
import { vdCompanyApi, vdDepartmentApi, vdDirectoryApi, vdRoutingApi, vdAIConfigApi } from '../api';

export default function VDDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      vdCompanyApi.get(),
      vdDepartmentApi.list(),
      vdDirectoryApi.list(),
      vdRoutingApi.list(),
      vdAIConfigApi.get(),
    ]).then(([company, depts, emps, rules, ai]) => {
      setData({ company, depts, emps, rules, ai });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-card/5 rounded-xl" />)}</div>;

  // Handle case where data is null or incomplete
  if (!data) {
    return (
      <div className="space-y-6 max-w-6xl" data-testid="vd-dashboard">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of your VoiceDesk IVR system</p>
        </div>
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-200">Complete onboarding to set up your VoiceDesk IVR</span>
            </div>
            <Button size="sm" onClick={() => navigate('/voicedesk/onboarding')} className="bg-amber-500 hover:bg-amber-600 text-black">
              Start Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { company, depts, emps, rules, ai } = data;
  const isActive = company?.status === 'active';

  const stats = [
    { label: 'Status', value: isActive ? 'Active' : 'Setup Required', icon: isActive ? CheckCircle2 : AlertCircle, color: isActive ? 'text-emerald-400' : 'text-amber-400' },
    { label: 'Business Number', value: company?.businessPhone || 'Not set', icon: Phone, color: 'text-blue-400' },
    { label: 'Departments', value: depts?.length || 0, icon: Building2, color: 'text-purple-400' },
    { label: 'Employees', value: emps?.length || 0, icon: Users, color: 'text-cyan-400' },
    { label: 'Routing Rules', value: rules?.length || 0, icon: GitBranch, color: 'text-orange-400' },
    { label: 'AI Service', value: ai?.enabled ? 'Connected' : 'Off', icon: Brain, color: ai?.enabled ? 'text-emerald-400' : 'text-muted-foreground' },
  ];

  const actions = [
    { label: 'Add Department', icon: Building2, to: '/voicedesk/departments' },
    { label: 'Add Employee', icon: Users, to: '/voicedesk/directory' },
    { label: 'Configure Routing', icon: GitBranch, to: '/voicedesk/routing' },
    { label: 'Test IVR', icon: PlayCircle, to: '/voicedesk/test' },
  ];

  return (
    <div className="space-y-6 max-w-6xl" data-testid="vd-dashboard">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Overview of your VoiceDesk IVR system</p>
      </div>

      {!isActive && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-200">Complete onboarding to activate your IVR</span>
            </div>
            <Button size="sm" onClick={() => navigate('/voicedesk/onboarding')} className="bg-amber-500 hover:bg-amber-600 text-black">
              Start Setup
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="bg-card/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
              <div className="text-lg font-semibold">{value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card className="bg-card/5 border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {actions.map(({ label, icon: Icon, to }) => (
            <Button key={label} variant="outline" size="sm" onClick={() => navigate(to)} className="border-white/10 text-foreground hover:text-foreground hover:bg-card/10">
              <Icon className="w-4 h-4 mr-1.5" />{label}
            </Button>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
